'use client';
import styles from './sportstrip.module.scss';
import IconAt from '@/assets/icons/icon-at.svg';
import IconMenuKebab from '@/assets/icons/icon-menu-kebab.svg';
import SXMLink from '@/shared-components/SXMLink';
import cn from 'classnames';
import { useEffect, useId, useRef, useState } from 'react';
import { PopoverWrapper } from '../../PopoverWrapper';
import IconCar from '@/assets/icons/icon-car.svg';
import IconDevices from '@/assets/icons/icon-devices.svg';
import { IconPlayButton } from '@/assets/icons/IconPlayButton';
import DynamicTable, { type DynamicTableRow } from '../../DynamicTable/DynamicTable';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { CSSPropsVars } from '@/interfaces/types/CSSPropsVars';
import { AudioIcon } from '@/ui-components/atoms/AnimatedIcons/AudioIcon/AudioIcon';
import { Text } from '@/ui-components/atoms/Text/Text';
import { LinkWrapperIfHref } from '@/ui-components/atoms/LinkWrapperIfHref/LinkWrapperIfHref';
import { ShowMore } from '@/ui-components/molecules/ShowMore/ShowMore';

export interface Details {
    logo?: AEMImage;
    color?: string;
    pageUrl?: string;
    playerUrl?: string;
    name: string;
    location: string;
    channel?: string | number;
}

interface SportStripProps {
    homeTeam?: Details;
    awayTeam?: Details;
    league?: Details;
    moreWays?: DynamicTableRow[];
    isLive: boolean;
    subStrip?: React.ReactNode;
    tabletContentLayout?: 'row' | 'column';
    ariaLabel?: string;
}

export default function SportStrip(props: SportStripProps): React.JSX.Element {
    const contentId = useId();

    const [showContent, setShowContent] = useState(false);

    const stripRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            if (!stripRef.current) return;

            if (!stripRef.current.contains(event.target as Node)) {
                setShowContent(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, []);

    const onPointerLeave = (e: React.PointerEvent) => {
        if (e.pointerType !== 'mouse') return;
        setShowContent(false);
    };

    const contentStyles = cn(styles.content, {
        [styles.tabletColumnLayout]: props.tabletContentLayout === 'column',
        [styles.league]: props.league,
        [styles.isLive]: props.isLive,
    });

    const stripStyles = cn(styles.strip, {
        [styles.hasSubStrip]: props.subStrip,
    });

    const iconStyles = {
        width: '28px',
        height: '28px',
        fill: 'var(--black)',
    };

    function renderLogo(logo?: AEMImage) {
        if (logo) {
            return (
                <ImageWrapper
                    src={logo.url}
                    alt={logo.altText}
                    optimizedWidth={logo.width}
                    optimizedHeight={logo.height}
                    maxWidth={90}
                />
            );
        }
    }

    function renderTeam(type: 'Away' | 'Home' | 'League', team: Details) {
        const isLeague = type === 'League';

        const detailsClasses = cn(styles.details, styles.contentSection, {
            [styles.fullWidth]: isLeague,
            [styles.isLive]: props.isLive,
            [styles.singleTeam]: !props.homeTeam,
        });
        const detailsCustomProperties = {
            '--sportsStripTeamColor': team.color,
        } as CSSPropsVars;

        const ariaLabel =
            type === 'Away'
                ? `${props.awayTeam?.name} @ ${props.homeTeam?.name}. Listen to ${props.awayTeam?.name} away channel ${team.channel}`
                : `${props.awayTeam?.name} @ ${props.homeTeam?.name}. Listen to ${props.homeTeam?.name} home channel ${team.channel}`;

        return (
            <LinkWrapperIfHref
                link={{
                    href: team.pageUrl,
                    target: '_blank',
                    'aria-label': `Listen to ${team.name}${team.channel ? ` on ${team.channel}` : ''}`,
                }}
                className={detailsClasses}
                style={detailsCustomProperties}
            >
                {(!isLeague || team.logo) && (
                    <div className={isLeague ? styles.leagueLogo : styles.teamLogo}>{renderLogo(team.logo)}</div>
                )}
                <div className={styles.detailsBody}>
                    <div className={styles.contentSection}>
                        <Text tag="div" variant="text-bold" className={styles.teamName}>
                            {team.name}
                        </Text>
                        {!isLeague && (
                            <Text
                                tag="div"
                                variant="text-small"
                                role="img"
                                ariaLabel={ariaLabel}
                                className={styles.eyebrowLink}
                            >
                                {type}{' '}
                                {team.channel && (
                                    <>
                                        &bull; {!isNaN(parseInt(`${team.channel}`)) ? 'CH' : ''} {team.channel}
                                    </>
                                )}
                            </Text>
                        )}
                        {isLeague && (
                            <Text tag="div" variant="text-small" className={styles.locationText}>
                                {team.location}
                            </Text>
                        )}
                    </div>
                    {!isLeague && renderListenLive('', team.name, team.playerUrl)}
                    {isLeague && props.league && (
                        <div className={styles.leagueDetails}>
                            <LinkWrapperIfHref
                                link={{
                                    href: props.league.playerUrl,
                                    target: '_blank',
                                    'aria-label': `Listen to ${props.league.name}${props.league.channel ? ` on ${props.league.channel}` : ''}`,
                                }}
                            >
                                <div className={styles.channel}>
                                    <Text tag="div" variant="text-small">
                                        {!isNaN(parseInt(`${props.league.channel}`)) ? 'CH' : ''} {props.league.channel}
                                    </Text>
                                </div>
                            </LinkWrapperIfHref>
                            {renderListenLive('Listen Live', props.league.name, props.league.playerUrl)}
                        </div>
                    )}
                </div>
            </LinkWrapperIfHref>
        );
    }

    function renderListenLive(buttonLabel: string, eventName: string, playerUrl?: string): React.JSX.Element {
        if (!props.isLive || !playerUrl) {
            return <></>;
        }

        return (
            <SXMLink
                href={playerUrl}
                target="_blank"
                aria-hidden // Duplicated by neighboring `LinkWrapperIfHref`s
                className={styles.listenLive}
            >
                <div className={styles.listenLiveLabel}>{buttonLabel}</div>
                <div className={styles.listenLiveIcon}>
                    <IconPlayButton />
                </div>
            </SXMLink>
        );
    }

    return (
        <div className={styles.focusContainer} data-cy="molecules-sports-strip">
            <div
                className={stripStyles}
                data-cy="molecules-sports-strip"
                onPointerLeave={onPointerLeave}
                ref={stripRef}
            >
                <div className={styles.container}>
                    <div className={contentStyles}>
                        {props.awayTeam && renderTeam('Away', props.awayTeam)}
                        {props.homeTeam && (
                            <>
                                <div className={styles.at}>
                                    <IconAt fill="var(--base-contrast-subtle)" aria-hidden={true} />
                                </div>
                                {renderTeam('Home', props.homeTeam)}
                            </>
                        )}

                        {props.league && <>{renderTeam('League', props.league)}</>}
                    </div>

                    {props.moreWays && (
                        <div className={styles.menu}>
                            {props.isLive && (
                                <div className={styles.audioIcon}>
                                    <AudioIcon />
                                </div>
                            )}

                            <ShowMore
                                className={styles.moreInfo}
                                buttonClassName={styles.moreInfoButton}
                                arrowClassName={styles.showMoreArrow}
                                showText={<Kebab />}
                                hideText={<Kebab />}
                                color="var(--base-contrast-accent, #0000eb)"
                                ariaLabel={props.ariaLabel}
                                activeToggleTextPosition="top"
                                id={contentId}
                                shownClassName={styles.moreInfoActive}
                                contentClassName={styles.moreInfoContent}
                                onClickHandler={() => setShowContent(true)}
                                isContentShown={showContent}
                                ariaHaspopup
                            >
                                <PopoverWrapper
                                    ariaLabel={`Close More ways to listen ${props.awayTeam?.name} @ ${props.homeTeam?.name} dropdown`}
                                    className={styles.popOver}
                                    contentPadding="16px"
                                    headerContent="More Ways to listen"
                                    onClosePress={() => setShowContent(false)}
                                >
                                    <DynamicTable
                                        columnLabels={[
                                            <IconCar key={0} style={iconStyles} aria-label="In the car" />,
                                            <IconDevices key={1} style={iconStyles} aria-label="On your device" />,
                                        ]}
                                        rows={props.moreWays}
                                    />
                                </PopoverWrapper>
                            </ShowMore>
                        </div>
                    )}
                </div>
                {props.subStrip}
            </div>
        </div>
    );
}

function Kebab() {
    return (
        <div className={styles.kebab}>
            <IconMenuKebab />
        </div>
    );
}
